import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import type { RevenueSlice } from "../types/analyticsDashboard";
import { formatCurrency, getRevenuePercent } from "../utils/revenueFormatting";

type PartnerRevenueLegendSectionProps = {
  revenueDistribution: RevenueSlice[];
  totalRevenue: number;
  hotelCount: number;
};

export const PartnerRevenueLegendSection = ({
  revenueDistribution,
  totalRevenue,
  hotelCount,
}: PartnerRevenueLegendSectionProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="w-full space-y-5">
      <div className="flex h-[50px] w-full flex-col items-center justify-start gap-1.5 text-center">
        <h3 className="h-6 w-full text-base font-medium leading-normal tracking-normal text-[#0A0A0A]">
          {t("analytics.admin.partnerLegend.title", "Partners revenue")}
        </h3>
        <p className="h-5 w-full text-center text-sm font-normal leading-normal tracking-normal text-[#737373]">
          {t("analytics.admin.partnerLegend.subtitle", {
            defaultValue: "Distribution across {{count}} hotels",
            count: hotelCount,
          })}
        </p>
      </div>

      <div className="flex h-[192px] w-full flex-col gap-2">
        {revenueDistribution.map((item) => {
          const percent = getRevenuePercent(item.value, totalRevenue);

          return (
            <div
              key={item.hotelId}
              className="grid h-8 grid-cols-[auto_1fr_auto_auto] items-center gap-3"
            >
              <span
                className="inline-block size-5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <p className="text-base font-medium leading-normal text-[#171717] no-underline">
                {item.name}
              </p>
              <p className="text-base font-semibold leading-normal text-[#171717]">
                {formatCurrency(item.value, item.currency)}
                <span className="ml-2 text-base font-normal text-[#737373]">
                  ({percent}%)
                </span>
              </p>
              <ExternalLink className="size-4 text-zinc-400" />
            </div>
          );
        })}
      </div>
    </div>
  );
};
